'use client';

import { useRef, useState } from 'react';

import { type NavigateOptions } from 'react-router';
import { useLocation, useSearchParams } from 'react-router';
import { useDebounce, useMount, useUpdateEffect } from 'shared/base';

export function useReactRouterSyncWithUrlParam<
  Value extends string | string[] | number | boolean | undefined,
>({
  defaultValue,
  getInitialValue = (paramValue) => paramValue as Value,
  paramKey,
  parseParamValue = (paramValue) => paramValue as Value,
}: {
  defaultValue: Value;
  getInitialValue?: (paramValue: string) => Value;
  isArray?: boolean;
  paramKey: string;
  parseParamValue?: (paramValue: string) => Value;
}) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue = searchParams.get(paramKey) || '';
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const [value, setValue] = useState(() => getInitialValue(paramValue) || defaultValue);

  const { debouncedFunc: debounceUpdateUrl } = useDebounce(
    ({ navigateOpts, value }: { navigateOpts?: NavigateOptions; value: Value }) => {
      if (pathnameRef.current !== location.pathname) {
        return;
      }

      // we extract the search params from the current URL
      // to make sure we get the latest values
      const searchParams = new URLSearchParams(searchParamsRef.current);
      const valueString = Array.isArray(value) ? value.join(',') : value?.toString();
      if (valueString && value !== defaultValue) {
        searchParams.set(paramKey, valueString);
        setSearchParams(searchParams, navigateOpts);
      } else {
        searchParams.delete(paramKey);
        setSearchParams(searchParams, navigateOpts);
      }
    },
    300,
  );

  const wrappedSetValue = (value: Value, updateOnlyState: boolean = false) => {
    setValue(value);

    if (updateOnlyState) {
      return {
        [paramKey]: value === defaultValue ? undefined : value,
      };
    }

    debounceUpdateUrl({
      value,
    });
  };

  // We need to update the URL to make sure
  // it reflects the initial value
  useMount(() => {
    if (value !== defaultValue) {
      debounceUpdateUrl({
        navigateOpts: { replace: true },
        value,
      });
    }
  });

  // We need to sync the value with the URL when the component is mounted
  // because the URL might have been updated outside of the app,
  // for example by history back/forward buttons
  useUpdateEffect(() => {
    const valueString = Array.isArray(value) ? value.join(',') : value?.toString();
    if (valueString !== paramValue) {
      setValue(parseParamValue(paramValue));
    }
  }, [paramValue]);

  return [value, wrappedSetValue] as const;
}

/**
 * This hook is used to update the search params in the URL.
 * It must be used when we want to update a multiple params in the URL and
 * wrappedSetValue must be used with updateOnlyState = true
 */
export function useReactRouterUpdateSearchParams() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const { debouncedFunc: debounceUpdateUrl } = useDebounce(
    (newParams: Record<string, string | string[] | number | undefined>) => {
      if (pathnameRef.current !== location.pathname) {
        return;
      }

      const searchParams = new URLSearchParams(searchParamsRef.current);
      for (const param in newParams) {
        if (newParams[param] === undefined) {
          searchParams.delete(param);
        } else {
          searchParams.set(
            param,
            Array.isArray(newParams[param])
              ? newParams[param].join(',')
              : newParams[param].toString(),
          );
        }
      }

      setSearchParams(searchParams);
    },
    300,
  );

  return debounceUpdateUrl;
}
